import React, { useEffect, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import CustomInput from 'src/app/shared/components/common/input'
import { useTranslation } from 'react-i18next'
import { Button, Flex } from 'antd'
import CustomCheckbox from 'src/app/shared/components/common/checkbox'
import { BODY_HEIGHT, HEADER_HEIGHT, INPUT_FIELD_HEIGHT, useFormRules } from '../index.constant'
import { FormValues, useAddNewIssuedCheckContext } from '../index.context'
import { colorTextGray1 } from 'src/app/themes/primaryThemeDefault'
import CustomSelect from 'src/app/shared/components/common/select'
import CustomDatePicker from 'src/app/shared/components/common/date-picker'
import { isEmpty } from 'lodash'
import { inputDecimalNumber, inputNumber } from 'src/app/shared/utils'
import { validateOnBlur } from 'src/app/shared/utils/String.util'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'

const AddNewIssuedCheckForm: React.FC = () => {
  const { form, clients, accounts, loading, disabled, onSubmit, loadingClients } = useAddNewIssuedCheckContext()
  const { control, handleSubmit } = useMemo(() => form, [form])
  const { t } = useTranslation()
  const { errors, isValid, isDirty } = useMemo(() => form.formState, [form.formState])
  const rules = useFormRules()
  const [isOpenPopupPicker, setIsOpenPopupPicker] = useState(false)
  const { mode } = useWindowSize()
  const [popupPickerPosition, setPopupPickerPosition] = useState({
    top: 0,
    bottom: 0,
    body: 0,
  })

  useEffect(() => {
    const body = document.querySelector('body')
    const bodyScreenHeight = body?.getBoundingClientRect()?.height ?? 0
    const topIssuedDate = HEADER_HEIGHT + BODY_HEIGHT
    const bottomIssuedDate = bodyScreenHeight - topIssuedDate - INPUT_FIELD_HEIGHT

    setPopupPickerPosition({
      top: topIssuedDate,
      bottom: bottomIssuedDate,
      body: bodyScreenHeight,
    })
  }, [isOpenPopupPicker])

  return (
    <div
      className='add-new-issued-check-form'
      style={{
        borderColor: colorTextGray1,
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 16,
        padding: 16,
        boxShadow: '0px 2px 4px #EAEBEC',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          gap={24}
          style={{
            flexDirection: 'column',
          }}
        >
          <Controller
            control={control}
            name='clientId'
            render={({ field, fieldState }) => (
              <CustomSelect
                showSearch={true}
                label={t('AddNewIssuedCheck:title.client_name')}
                placeholder={t('AddNewIssuedCheck:placeholder.client_name')}
                value={field.value}
                onChange={field.onChange}
                onBlur={() => validateOnBlur<FormValues>('clientId', field, form)}
                error={fieldState.error}
                options={
                  clients?.map((item) => ({
                    label: item.clientName,
                    value: item.id,
                  })) ?? []
                }
                loading={loadingClients}
                disabled={disabled || loadingClients}
                required={(rules.clientId.required as any).value}
              />
            )}
            rules={rules.clientId}
          />
          <Flex
            style={{
              flexDirection: ['mobile', 'tablet'].includes(mode) ? 'column' : undefined,
            }}
            gap={24}
          >
            <Controller
              control={control}
              name='accountId'
              render={({ field, fieldState }) => (
                <CustomSelect
                  showSearch={true}
                  label={t('AddNewIssuedCheck:title.account_name')}
                  placeholder={t('AddNewIssuedCheck:placeholder.account_name')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => validateOnBlur<FormValues>('accountId', field, form)}
                  error={fieldState.error}
                  options={accounts.map((item) => ({
                    label: item.accountName,
                    value: item.accountId,
                  }))}
                  loading={loadingClients}
                  disabled={loadingClients}
                  required={(rules.accountId.required as any).value}
                />
              )}
              rules={rules.accountId}
            />
            <Controller
              control={control}
              name='checkNumber'
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AddNewIssuedCheck:title.check_number')}
                  placeholder={t('AddNewIssuedCheck:placeholder.check_number')}
                  value={field.value}
                  onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                  onBlur={() => validateOnBlur<FormValues>('checkNumber', field, form)}
                  error={fieldState.error}
                  required={(rules.checkNumber.required as any).value}
                  maxLength={(rules.checkNumber.maxLength as any).value}
                />
              )}
              rules={rules.checkNumber}
            />
            <Controller
              control={control}
              name='amount'
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AddNewIssuedCheck:title.amount')}
                  placeholder={t('AddNewIssuedCheck:placeholder.amount')}
                  value={field.value}
                  onChange={(e) => field.onChange(inputDecimalNumber(e), { shouldValidate: true })}
                  onBlur={() => validateOnBlur<FormValues>('amount', field, form)}
                  error={fieldState.error}
                  required={(rules.amount.required as any).value}
                />
              )}
              rules={rules.amount}
            />
          </Flex>
          <Flex
            style={{
              flexDirection: ['mobile', 'tablet'].includes(mode) ? 'column' : undefined,
            }}
            gap={24}
          >
            <Controller
              control={control}
              name='issuedDate'
              render={({ field, fieldState }) => (
                <CustomDatePicker
                  label={t('AddNewIssuedCheck:title.issued_date')}
                  placeholder={t('AddNewIssuedCheck:placeholder.issued_date')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => validateOnBlur<FormValues>('issuedDate', field, form)}
                  error={fieldState.error}
                  required={(rules.issuedDate.required as any).value}
                  placement='bottomLeft'
                  popupClassName='add-new-issued-popup'
                  onClick={() => {
                    setIsOpenPopupPicker(true)
                  }}
                  className='issued-date-picker'
                />
              )}
              rules={rules.issuedDate}
            />
            <Controller
              control={control}
              name='issuedPayee'
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AddNewIssuedCheck:title.issued_payee')}
                  placeholder={t('AddNewIssuedCheck:placeholder.issued_payee')}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange({
                      ...e,
                      target: {
                        ...e.target,
                        value: e.target.value?.trimStart() ?? '',
                      },
                    })
                  }}
                  onBlur={(e) => {
                    field.onChange({
                      ...e,
                      target: {
                        ...e.target,
                        value: e.target.value?.trim() ?? '',
                      },
                    })
                    validateOnBlur<FormValues>('issuedPayee', field, form)
                  }}
                  error={fieldState.error}
                  required={(rules.issuedPayee.required as any).value}
                  maxLength={(rules.issuedPayee.maxLength as any).value}
                />
              )}
              rules={rules.issuedPayee}
            />
            <div
              style={{
                flex: 1,
              }}
            />
          </Flex>
          <Controller
            control={control}
            name='autoIncrement'
            render={({ field, fieldState }) => (
              <CustomCheckbox
                label={t('AddNewIssuedCheck:title.auto_increment')}
                checked={!!field.value}
                onChange={field.onChange}
                error={fieldState.error}
              />
            )}
          />
        </Flex>
        <Flex
          style={{
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type='primary'
            className='add-check-btn'
            disabled={!isValid || !isEmpty(errors) || !isDirty || loading}
            htmlType='button'
            onClick={handleSubmit(onSubmit)}
            loading={loading}
          >
            {t('AddNewIssuedCheck:title.add_check')}
          </Button>
        </Flex>
      </form>
    </div>
  )
}

export default AddNewIssuedCheckForm
